/**
 * Full-page branded surface for route-level states that replace the whole app
 * shell — the router's `errorElement` renders in place of everything, so these
 * states own the entire viewport rather than sitting inside a page.
 *
 * One frame, composed three ways (recovering / update-available / unexpected
 * error). Presentational only: it holds layout, typography and motion, and
 * takes its icon, copy and actions from the caller. Token-driven throughout, so
 * it is correct in light and dark with zero `dark:` variants.
 */

import type { ReactNode } from "react"

import { Icon } from "@/common/icons/Icon"
import type { IconName } from "@/common/icons/Icon.types"
import { cn } from "@/common/utils/cn.util"

/**
 * The circular frame that heads every surface — holds a `FeaturedIcon` or a
 * spinner. Owning the chrome in one place keeps the two media paths from
 * drifting apart on later polish.
 *
 * `decorative` marks the frame `aria-hidden` for the spinner path: the surface
 * itself is the live region announcing the message, so the spinner's own
 * `role="status"` would be a redundant nested one.
 */
export function FeaturedMedia({
  children,
  decorative
}: {
  children: ReactNode
  decorative?: boolean
}) {
  return (
    <div
      aria-hidden={decorative || undefined}
      className="rounded-nx-full bg-nx-surface-bg-tertiary flex size-14 items-center justify-center"
    >
      {children}
    </div>
  )
}

/**
 * The featured icon that heads each surface. `error` tints the glyph with the
 * error token; `neutral` keeps it calm — the default, because most of these
 * states are recoverable and do not warrant an alarm.
 */
export function FeaturedIcon({
  name,
  tone = "neutral"
}: {
  name: IconName
  tone?: "neutral" | "error"
}) {
  return (
    <FeaturedMedia>
      <Icon
        name={name}
        size={24}
        className={cn(tone === "error" ? "text-nx-text-error" : "text-nx-text-secondary")}
      />
    </FeaturedMedia>
  )
}

interface ErrorSurfaceProps {
  /** Heads the surface — a `FeaturedIcon`, or a spinner for an in-progress state. */
  media: ReactNode
  title: string
  description?: string
  /** Action buttons — `NxButton`s. Omitted for states with nothing to act on. */
  actions?: ReactNode
  /**
   * Announce the surface to assistive tech as a live status. Set on transient
   * states (recovering) so a screen reader hears them; the resting error states
   * are already in the reading order.
   */
  live?: boolean
}

export function ErrorSurface({ media, title, description, actions, live }: ErrorSurfaceProps) {
  return (
    <div
      className="bg-nx-surface-bg-base flex min-h-screen flex-col items-center justify-center px-6 py-12"
      role={live ? "status" : undefined}
      aria-live={live ? "polite" : undefined}
    >
      <div className="animate-in fade-in-0 zoom-in-95 flex w-full max-w-sm flex-col items-center gap-5 text-center duration-500 motion-reduce:animate-none">
        {media}
        <div className="flex flex-col gap-2">
          <h1 className="text-nx-text-primary text-xl font-semibold">{title}</h1>
          {description && (
            <p className="text-nx-text-tertiary text-sm leading-relaxed">{description}</p>
          )}
        </div>
        {actions && (
          <div className="mt-1 flex flex-wrap items-center justify-center gap-3">{actions}</div>
        )}
      </div>
    </div>
  )
}
